/**
 * @file mouse-performance.ts
 * @description Telink 3228C 维盛 AP 鼠标已确认参数功能区字段实现。
 */

import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import { mousePerfCfgFeature } from '../../../../features/mouse-performance/contract'
import type { BaseDevice } from '../../../../runtime/BaseDevice'
import { ConnectionType } from '../../../../types/connection'
import type {
  Correction,
  GlassSurfaceSwitch,
  KeyDebounceTime,
  KeyReleaseDebounceTime,
  LowLightEffectSwitch,
  MotionSynSwitch,
  MousePerfCfg,
  ReportRate,
  RfBoostSwitch,
  SilentHeight,
  SleepTime,
  SensorAngle,
  SensorMode,
  SensorModes,
} from '../../../../types/mousePerfCfg'
import {
  VISION_AP_MOUSE_FUNCTION_AREA,
  type VisionApFunctionAreaSession,
} from '../../protocols/vision-ap-mouse'

const FUNCTION_AREA_LENGTH = VISION_AP_MOUSE_FUNCTION_AREA.byteLength
const SLEEP_TIME_OFFSET = 77
const LIFT_OFF_OFFSET = 78
const ANGLE_SNAP_OFFSET = 79
const RIPPLE_CONTROL_OFFSET = 80
const MOTION_SYNC_OFFSET = 81
const WIRELESS_REPORT_RATE_OFFSET = 10
const WIRED_REPORT_RATE_OFFSET = 11
const SENSOR_ANGLE_OFFSET = 82
const SENSOR_MODE_OFFSETS = [89, 88, 87, 86, 85, 84, 83] as const
const FULL_POWER_RF_OFFSET = 90
const KEY_PRESS_DEBOUNCE_OFFSET = 92
const KEY_RELEASE_DEBOUNCE_OFFSET = 93
const GLASS_TRACKING_OFFSET = 94
const LOW_BATTERY_INDICATOR_OFFSET = VISION_AP_MOUSE_FUNCTION_AREA.lowBatteryIndicatorOffset

// 协议值以性能由高到低编码，Web contract 以办公到狂暴递增，必须在边界层转换。
const RAW_SENSOR_MODE_TO_CONTRACT: Readonly<Record<number, SensorMode>> = {
  0: 3,
  1: 4,
  2: 5,
  3: 2,
  4: 1,
  5: 0,
}

const CONTRACT_SENSOR_MODE_TO_RAW: Readonly<Record<SensorMode, number>> = {
  0: 5,
  1: 4,
  2: 3,
  3: 0,
  4: 1,
  5: 2,
}

// 固件功能区保存的是 0..6 档位，Web contract 则沿用跨芯片的回报率编码。
type VisionApReportRate = Exclude<ReportRate, 0x00 | 0x03>

const RAW_REPORT_RATE_TO_CONTRACT = new Map<number, VisionApReportRate>([
  [0, 0x08],
  [1, 0x04],
  [2, 0x02],
  [3, 0x01],
  [4, 0x84],
  [5, 0x82],
  [6, 0x81],
])
const CONTRACT_REPORT_RATE_TO_RAW = new Map<ReportRate, number>([
  [0x08, 0],
  [0x04, 1],
  [0x02, 2],
  [0x01, 3],
  [0x84, 4],
  [0x82, 5],
  [0x81, 6],
])

interface TelinkApMousePerfDevice extends BaseDevice {
  getConnectionType(): ConnectionType
  runFunctionAreaSession<T>(
    operation: (session: VisionApFunctionAreaSession) => Promise<T>,
  ): Promise<T>
  updateFunctionArea(update: (area: Uint8Array) => void): Promise<void>
}

export interface Telink3228CReportRateSnapshot {
  reportRate: VisionApReportRate
  sensorMode: SensorMode
}

/** 仅适配已由协议和同型号实现共同确认的参数项。 */
export class Telink3228CMousePerfCfg extends mousePerfCfgFeature {
  private readonly apDevice: TelinkApMousePerfDevice

  constructor(device: TelinkApMousePerfDevice) {
    super(device)
    this.apDevice = device
  }

  public async getMoveSync(): Promise<MotionSynSwitch> {
    return (await this.readArea())[MOTION_SYNC_OFFSET] === 0 ? 0 : 1
  }

  public async setMoveSync(value: MotionSynSwitch): Promise<void> {
    this.assertSwitch(value, 'Motion Sync')
    await this.updateArea((area) => {
      area[MOTION_SYNC_OFFSET] = value
    })
  }

  public async getCorrect(): Promise<Correction> {
    const area = await this.readArea()
    return {
      // AP 协议 1=启用；现有 Web contract 0=启用，适配层在此统一语义。
      linearCorrectionSwitch: area[ANGLE_SNAP_OFFSET] === 0 ? 1 : 0,
      rippleCorrectionSwitch: area[RIPPLE_CONTROL_OFFSET] === 0 ? 1 : 0,
    }
  }

  public async setCorrect(value: Correction): Promise<void> {
    this.assertSwitch(value.linearCorrectionSwitch, '直线修正')
    this.assertSwitch(value.rippleCorrectionSwitch, '波纹修正')
    await this.updateArea((area) => {
      area[ANGLE_SNAP_OFFSET] = value.linearCorrectionSwitch === 0 ? 1 : 0
      area[RIPPLE_CONTROL_OFFSET] = value.rippleCorrectionSwitch === 0 ? 1 : 0
    })
  }

  public async getSleepTime(): Promise<SleepTime> {
    return this.decodeSleepTime((await this.readArea())[SLEEP_TIME_OFFSET])
  }

  public async setSleepTime(value: SleepTime): Promise<void> {
    this.assertSleepTime(value)
    await this.updateArea((area) => {
      area[SLEEP_TIME_OFFSET] = this.encodeSleepTime(value)
    })
  }

  public async getSensorAngle(): Promise<SensorAngle> {
    return (await this.readArea())[SENSOR_ANGLE_OFFSET] - 30
  }

  public async setSensorAngle(value: SensorAngle): Promise<void> {
    if (!Number.isInteger(value) || value < -30 || value > 30) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '传感器角度必须为 -30 至 30 的整数')
    }
    await this.updateArea((area) => {
      area[SENSOR_ANGLE_OFFSET] = value + 30
    })
  }

  public async getGlassTrack(): Promise<GlassSurfaceSwitch> {
    return (await this.readArea())[GLASS_TRACKING_OFFSET] === 0 ? 0 : 1
  }

  public async setGlassTrack(value: GlassSurfaceSwitch): Promise<void> {
    this.assertSwitch(value, '玻璃追踪')
    await this.updateArea((area) => {
      area[GLASS_TRACKING_OFFSET] = value
    })
  }

  public async getDebounceTime(): Promise<KeyDebounceTime> {
    return ((await this.readArea())[KEY_PRESS_DEBOUNCE_OFFSET] - 2) as KeyDebounceTime
  }

  public async setDebounceTime(value: KeyDebounceTime): Promise<void> {
    this.assertDebounce(value, '按下去抖')
    await this.updateArea((area) => {
      area[KEY_PRESS_DEBOUNCE_OFFSET] = value + 2
    })
  }

  public async getReleaseDebounceTime(): Promise<KeyReleaseDebounceTime> {
    return ((await this.readArea())[KEY_RELEASE_DEBOUNCE_OFFSET] - 2) as KeyReleaseDebounceTime
  }

  public async setReleaseDebounceTime(value: KeyReleaseDebounceTime): Promise<void> {
    this.assertDebounce(value, '抬起去抖')
    await this.updateArea((area) => {
      area[KEY_RELEASE_DEBOUNCE_OFFSET] = value + 2
    })
  }

  public async getSilentHeight(): Promise<SilentHeight> {
    return ((await this.readArea())[LIFT_OFF_OFFSET] + 1) as SilentHeight
  }

  public async setSilentHeight(value: SilentHeight): Promise<void> {
    if (!Number.isInteger(value) || value < 1 || value > 11) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'LOD 必须为 1-11 的整数')
    }
    await this.updateArea((area) => {
      area[LIFT_OFF_OFFSET] = value - 1
    })
  }

  public async getWirelessRadioPolicy(): Promise<RfBoostSwitch> {
    return (await this.readArea())[FULL_POWER_RF_OFFSET] === 0 ? 0 : 1
  }

  public async setWirelessRadioPolicy(value: RfBoostSwitch): Promise<void> {
    this.assertSwitch(value, 'RF 强化')
    await this.updateArea((area) => {
      area[FULL_POWER_RF_OFFSET] = value
    })
  }

  public async getLowBatteryLight(): Promise<LowLightEffectSwitch> {
    return this.decodeSwitch(
      (await this.readArea())[LOW_BATTERY_INDICATOR_OFFSET],
      '低电量功能指示',
    )
  }

  public async setLowBatteryLight(value: LowLightEffectSwitch): Promise<void> {
    this.assertSwitch(value, '低电量功能指示')
    await this.updateArea((area) => {
      area[LOW_BATTERY_INDICATOR_OFFSET] = value
    })
  }

  public async getReportRate(): Promise<ReportRate> {
    return this.decodeReportRate((await this.readArea())[this.getReportRateOffset()])
  }

  /** 从同一份功能区快照解析当前回报率及其对应的传感器模式。 */
  public parseCurrentReportRateSnapshot(area: Uint8Array): Telink3228CReportRateSnapshot {
    if (area.byteLength !== FUNCTION_AREA_LENGTH) {
      throw new DeviceError(DeviceErrorCode.READ_FAILED, '性能参数功能区长度不正确')
    }
    const rawRate = area[this.getReportRateOffset()]
    return {
      reportRate: this.decodeReportRate(rawRate),
      sensorMode: this.decodeSensorMode(area[this.getSensorModeOffset(rawRate)]),
    }
  }

  public async setReportRate(rate: ReportRate): Promise<void> {
    const rawRate = this.encodeReportRate(rate)
    const offset = this.getReportRateOffset()
    await this.updateArea((area) => {
      area[offset] = rawRate
    })
  }

  public async getSensorMode(index: number): Promise<SensorMode> {
    return this.decodeSensorMode((await this.readArea())[this.getSensorModeOffset(index)])
  }

  public async setSensorMode(index: number, mode: SensorMode): Promise<void> {
    const offset = this.getSensorModeOffset(index)
    const rawMode = this.encodeSensorMode(mode)
    await this.updateArea((area) => {
      area[offset] = rawMode
    })
  }

  public async getSensorModeList(): Promise<SensorModes> {
    const area = await this.readArea()
    return SENSOR_MODE_OFFSETS.map((offset) => this.decodeSensorMode(area[offset]))
  }

  public async setSensorModeList(modes: SensorModes): Promise<void> {
    if (!Array.isArray(modes) || modes.length !== SENSOR_MODE_OFFSETS.length) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '传感器模式列表必须包含 7 个回报率档位')
    }
    const rawModes = modes.map((mode) => this.encodeSensorMode(mode))
    await this.updateArea((area) => {
      SENSOR_MODE_OFFSETS.forEach((offset, index) => {
        area[offset] = rawModes[index]
      })
    })
  }

  public async getAllPerfCfg(): Promise<MousePerfCfg> {
    return this.toPerformanceConfig(await this.readArea())
  }

  public async setAllPerfCfg(config: MousePerfCfg): Promise<void> {
    await this.updateArea((area) => {
      if (config.reportRate !== undefined) {
        area[this.getReportRateOffset()] = this.encodeReportRate(config.reportRate)
      }
      if (config.motionSynSwitch !== undefined) {
        this.assertSwitch(config.motionSynSwitch, 'Motion Sync')
        area[MOTION_SYNC_OFFSET] = config.motionSynSwitch
      }
      if (
        config.linearCorrectionSwitch !== undefined ||
        config.rippleCorrectionSwitch !== undefined
      ) {
        if (
          config.linearCorrectionSwitch === undefined ||
          config.rippleCorrectionSwitch === undefined
        ) {
          throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '直线修正与波纹修正必须同时写入')
        }
        this.assertSwitch(config.linearCorrectionSwitch, '直线修正')
        this.assertSwitch(config.rippleCorrectionSwitch, '波纹修正')
        area[ANGLE_SNAP_OFFSET] = config.linearCorrectionSwitch === 0 ? 1 : 0
        area[RIPPLE_CONTROL_OFFSET] = config.rippleCorrectionSwitch === 0 ? 1 : 0
      }
      if (config.sleepTime !== undefined) {
        this.assertSleepTime(config.sleepTime)
        area[SLEEP_TIME_OFFSET] = this.encodeSleepTime(config.sleepTime)
      }
      if (config.sensorAngle !== undefined) {
        if (
          !Number.isInteger(config.sensorAngle) ||
          config.sensorAngle < -30 ||
          config.sensorAngle > 30
        ) {
          throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '传感器角度必须为 -30 至 30 的整数')
        }
        area[SENSOR_ANGLE_OFFSET] = config.sensorAngle + 30
      }
      if (config.silentHeight !== undefined) {
        if (
          !Number.isInteger(config.silentHeight) ||
          config.silentHeight < 1 ||
          config.silentHeight > 11
        ) {
          throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'LOD 必须为 1-11 的整数')
        }
        area[LIFT_OFF_OFFSET] = config.silentHeight - 1
      }
      if (config.glassSurfaceSwitch !== undefined) {
        this.assertSwitch(config.glassSurfaceSwitch, '玻璃追踪')
        area[GLASS_TRACKING_OFFSET] = config.glassSurfaceSwitch
      }
      if (config.keyDebounceTime !== undefined) {
        this.assertDebounce(config.keyDebounceTime, '按下去抖')
        area[KEY_PRESS_DEBOUNCE_OFFSET] = config.keyDebounceTime + 2
      }
      if (config.keyReleaseDebounceTime !== undefined) {
        this.assertDebounce(config.keyReleaseDebounceTime, '抬起去抖')
        area[KEY_RELEASE_DEBOUNCE_OFFSET] = config.keyReleaseDebounceTime + 2
      }
      if (config.rfBoostSwitch !== undefined) {
        this.assertSwitch(config.rfBoostSwitch, 'RF 强化')
        area[FULL_POWER_RF_OFFSET] = config.rfBoostSwitch
      }
      if (config.lowLightEffectSwitch !== undefined) {
        this.assertSwitch(config.lowLightEffectSwitch, '低电量功能指示')
        area[LOW_BATTERY_INDICATOR_OFFSET] = config.lowLightEffectSwitch
      }
    })
  }

  private async readArea(): Promise<Uint8Array> {
    return this.apDevice.runFunctionAreaSession((session) => session.readFunctionAreaSnapshot())
  }

  private async updateArea(update: (area: Uint8Array) => void): Promise<void> {
    await this.apDevice.updateFunctionArea(update)
  }

  private toPerformanceConfig(area: Uint8Array): MousePerfCfg {
    if (area.byteLength !== FUNCTION_AREA_LENGTH) {
      throw new DeviceError(DeviceErrorCode.READ_FAILED, '性能参数功能区长度不正确')
    }
    return {
      reportRate: this.decodeReportRate(area[this.getReportRateOffset()]),
      motionSynSwitch: area[MOTION_SYNC_OFFSET] === 0 ? 0 : 1,
      linearCorrectionSwitch: area[ANGLE_SNAP_OFFSET] === 0 ? 1 : 0,
      rippleCorrectionSwitch: area[RIPPLE_CONTROL_OFFSET] === 0 ? 1 : 0,
      sleepTime: this.decodeSleepTime(area[SLEEP_TIME_OFFSET]),
      sensorAngle: area[SENSOR_ANGLE_OFFSET] - 30,
      silentHeight: (area[LIFT_OFF_OFFSET] + 1) as SilentHeight,
      glassSurfaceSwitch: area[GLASS_TRACKING_OFFSET] === 0 ? 0 : 1,
      keyDebounceTime: (area[KEY_PRESS_DEBOUNCE_OFFSET] - 2) as KeyDebounceTime,
      keyReleaseDebounceTime: (area[KEY_RELEASE_DEBOUNCE_OFFSET] - 2) as KeyReleaseDebounceTime,
      rfBoostSwitch: area[FULL_POWER_RF_OFFSET] === 0 ? 0 : 1,
      lowLightEffectSwitch: this.decodeSwitch(area[LOW_BATTERY_INDICATOR_OFFSET], '低电量功能指示'),
    }
  }

  private decodeSwitch(value: number, field: string): 0 | 1 {
    if (value !== 0 && value !== 1) {
      throw new DeviceError(DeviceErrorCode.READ_FAILED, `${field}读取到未定义值: ${value}`)
    }
    return value
  }

  private assertSwitch(value: number, field: string): void {
    if (value !== 0 && value !== 1) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, `${field}只能为 0 或 1`)
    }
  }

  private assertDebounce(value: number, field: string): void {
    if (!Number.isInteger(value) || value < 0 || value > 253) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, `${field}必须为 0-253 的整数`)
    }
  }

  private assertSleepTime(value: SleepTime): void {
    if (!Number.isInteger(value) || !((value >= 5 && value <= 120) || value === 255)) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        '休眠时间必须为 5-120 分钟或 255（永不休眠）',
      )
    }
  }

  private encodeSleepTime(value: SleepTime): number {
    return value === 255 ? 250 : value - 5
  }

  private decodeSleepTime(rawValue: number): SleepTime {
    return rawValue === 250 ? 255 : ((rawValue + 5) as SleepTime)
  }

  private getReportRateOffset(): number {
    return this.apDevice.getConnectionType() === ConnectionType.Wireless
      ? WIRELESS_REPORT_RATE_OFFSET
      : WIRED_REPORT_RATE_OFFSET
  }

  private decodeReportRate(rawRate: number): VisionApReportRate {
    const rate = RAW_REPORT_RATE_TO_CONTRACT.get(rawRate)
    if (rate === undefined) {
      throw new DeviceError(
        DeviceErrorCode.READ_FAILED,
        `回报率原始档位必须为 0-6，实际为: 0x${rawRate.toString(16)}`,
      )
    }
    return rate
  }

  private encodeReportRate(rate: number): number {
    const rawRate = CONTRACT_REPORT_RATE_TO_RAW.get(rate as ReportRate)
    if (rawRate === undefined) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        `不支持的回报率值: 0x${rate.toString(16)}`,
      )
    }
    return rawRate
  }

  private getSensorModeOffset(index: number): number {
    if (!Number.isInteger(index) || index < 0 || index >= SENSOR_MODE_OFFSETS.length) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '传感器模式索引必须为 0-6 的整数')
    }
    return SENSOR_MODE_OFFSETS[index]
  }

  private decodeSensorMode(rawMode: number): SensorMode {
    const mode = RAW_SENSOR_MODE_TO_CONTRACT[rawMode]
    if (mode === undefined) {
      throw new DeviceError(DeviceErrorCode.READ_FAILED, `传感器模式原始值无效: ${rawMode}`)
    }
    return mode
  }

  private encodeSensorMode(mode: number): number {
    if (!Number.isInteger(mode) || !(mode in CONTRACT_SENSOR_MODE_TO_RAW)) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '传感器模式必须为 0-5 的整数')
    }
    return CONTRACT_SENSOR_MODE_TO_RAW[mode as SensorMode]
  }
}
